import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useEffectiveTimezone } from "@/hooks/use-timezone";
import type { Contact } from "@shared/schema";
import { insertContactSchema } from "@shared/schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Search } from "lucide-react";
import { CountryPhoneInput } from "@/components/country-phone-input";

export const contactFormSchema = insertContactSchema.omit({ tenantId: true }).extend({
  name: z.string().min(1, "Name is required"),
  phoneNumber: z.string().min(1, "Phone number is required").regex(/^\+?[0-9]+$/, "Phone number may only contain digits and an optional leading +"),
});

export type ContactFormValues = z.infer<typeof contactFormSchema>;

function ListSelector({
  lists,
  selectedListIds,
  setSelectedListIds,
}: {
  lists: { id: string; name: string }[];
  selectedListIds: string[];
  setSelectedListIds: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const [listSearch, setListSearch] = useState("");
  const filtered = lists.filter((l) =>
    !listSearch || l.name.toLowerCase().includes(listSearch.toLowerCase())
  );

  return (
    <div className="space-y-2">
      <Label>Add to Lists</Label>
      {lists.length > 5 && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search lists..."
            value={listSearch}
            onChange={(e) => setListSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
            data-testid="input-search-lists"
          />
        </div>
      )}
      <div className="max-h-32 overflow-auto space-y-1.5 border rounded-md p-2">
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-2">No lists found</p>
        ) : (
          filtered.map((list) => (
            <div key={list.id} className="flex items-center gap-2">
              <Checkbox
                id={`list-${list.id}`}
                checked={selectedListIds.includes(list.id)}
                onCheckedChange={(checked) => {
                  setSelectedListIds((prev) =>
                    checked ? [...prev, list.id] : prev.filter((id) => id !== list.id)
                  );
                }}
                data-testid={`checkbox-list-${list.id}`}
              />
              <label htmlFor={`list-${list.id}`} className="text-sm cursor-pointer">{list.name}</label>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function ContactForm({
  onSuccess,
  editContact,
}: {
  onSuccess: () => void;
  editContact?: Contact;
}) {
  const { toast } = useToast();
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const { data: tenantSettings } = useQuery<{ defaultCountryCode?: string }>({
    queryKey: ["/api/tenant-settings"],
  });

  useEffectiveTimezone();

  const { data: listsData } = useQuery<{ id: string; name: string; contactCount: number }[]>({
    queryKey: ["/api/contact-lists"],
    enabled: !editContact,
  });
  const lists = listsData || [];

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: editContact?.name || "",
      phoneNumber: editContact?.phoneNumber || "",
      email: editContact?.email || "",
      company: editContact?.company || "",
      jobTitle: editContact?.jobTitle || "",
      notes: editContact?.notes || "",
      favorite: editContact?.favorite || false,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (values: ContactFormValues) => {
      if (editContact) {
        return apiRequest("PATCH", `/api/contacts/${editContact.id}`, values);
      }
      const res = await apiRequest("POST", "/api/contacts", values);
      const contact = await res.json();
      if (selectedListIds.length > 0) {
        for (const listId of selectedListIds) {
          try {
            await apiRequest("POST", `/api/contact-lists/${listId}/contacts`, { contactId: contact.id });
          } catch {}
        }
      }
      return contact;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (query) => (query.queryKey[0] as string).startsWith("/api/contacts") });
      queryClient.invalidateQueries({ queryKey: ["/api/contact-lists"] });
      toast({ title: editContact ? "Contact updated" : "Contact created" });
      setSelectedListIds([]);
      onSuccess();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((v) => createMutation.mutate(v))} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} data-testid="input-contact-name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <CountryPhoneInput
                  value={field.value || ""}
                  onChange={field.onChange}
                  defaultCountryCode={tenantSettings?.defaultCountryCode}
                  placeholder="6137005053"
                  inputTestId="input-contact-phone"
                  countryTestId="button-contact-phone-country"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="john@example.com" {...field} value={field.value || ""} data-testid="input-contact-email" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="company"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company</FormLabel>
              <FormControl>
                <Input placeholder="Acme Inc." {...field} value={field.value || ""} data-testid="input-contact-company" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="jobTitle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Job Title</FormLabel>
              <FormControl>
                <Input placeholder="Software Engineer" {...field} value={field.value || ""} data-testid="input-contact-job-title" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea placeholder="Notes about this contact..." {...field} value={field.value || ""} data-testid="input-contact-notes" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {!editContact && lists.length > 0 && (
          <ListSelector
            lists={lists}
            selectedListIds={selectedListIds}
            setSelectedListIds={setSelectedListIds}
          />
        )}
        <Button
          type="submit"
          className="w-full"
          disabled={createMutation.isPending}
          data-testid="button-save-contact"
        >
          {createMutation.isPending ? "Saving..." : editContact ? "Update Contact" : "Add Contact"}
        </Button>
      </form>
    </Form>
  );
}
